/**
 * 入住换房
 */
import React, { useEffect, useRef, memo } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Button } from 'antd';
import intl from 'react-intl-universal';

import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import usePortalConfirm from '@/components/PortalConfirm';
import PortalLoading from '@/components/PortalLoading';
import reserve from '@/api/reserve';
import { OrderDetailTabKey } from '@/pages/order/detail/component/DetailInfo';
import PreSettleDrawer, { PreSettleDrawerRef } from '@/components/Reservation/PreSettleDrawer';
import { showOutdateConfirm, showLeaveConfirm } from '@/components/Reservation/Modal';

import PopupTarget, { TargetType } from '@/components/Reservation/PopupTarget';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import {
  initPageState,
  setLoadingStatus,
  getMainState,
  refreshOutdateRoomInfo,
} from '@/redux/actions/reservation/reserveExchange';
import { ReducerState, storeName } from '@/redux/reducers/reservation/reserveExchange';
import { ExchangeRentRoomInfo } from '@/redux/reducers/reservation/base.d';
import useOrderLink from '@/hooks/useOrderLink';

import RoomInfo from './components/RoomInfo';
import RentRoomList from './components/RentRoomList';
import SubmitBtn from './components/SubmitBtn';
import validator from './validator';
import * as helper from './helper';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading } = state;
  return { isInit, isLoading };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitState: initPageState,
  callSetLoading: setLoadingStatus,
  callGetState: getMainState,
  callRefreshRoomInfo: refreshOutdateRoomInfo,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IConfig {
  recNo: string; // 订单id
  targetRoom?: Partial<ExchangeRentRoomInfo>; // 默认换房房间
  onEnd?: (result: boolean) => void;
}
interface IProps extends IConfig, MapStateProps, MapDispatchProps {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const { unmountNode, onEnd, isInit, isLoading, callSetLoading, callGetState } = props;

  const confirmPortal = usePortalConfirm();
  const preSettleRef = useRef<PreSettleDrawerRef>(null);
  const { linkToReservationDetail } = useOrderLink();

  // 抽屉关闭回调
  const callCloseCallback = (result) => {
    if (typeof onEnd === 'function') {
      try {
        onEnd(result);
      } catch (e) {
        //
      }
    }
  };

  // 取消
  const handleEditCancel = async () => {
    callGetState(async (state) => {
      const { isModify } = state;
      // 数据有变动
      if (isModify) {
        const isConfirm = await showLeaveConfirm(confirmPortal); // 是否确认离开
        if (!isConfirm) return;
        unmountNode();
      } else {
        unmountNode();
      }
      callCloseCallback(false);
    });
  };

  // 房间状态变动 需要重新刷新房间数据
  const handleRefreshErr: (response) => void = async (response) => {
    // 房间变动
    await showOutdateConfirm(confirmPortal, response, 'reserve').then((shouldRefresh) => {
      if (shouldRefresh) {
        const { callRefreshRoomInfo } = props;
        callRefreshRoomInfo();
      }
    });
  };

  // 预结算抽屉
  const showPreSettleDrawer = () => {
    callGetState((state) => {
      const orderNo = state.commonInfo.mainOrderId;
      preSettleRef.current.showDrawer(orderNo, () => {
        message.success(intl.get('drawer_exchange_message_update_success'));
        unmountNode();
        linkToReservationDetail(orderNo, {
          tabKey: OrderDetailTabKey.RoomKey,
        });
        callCloseCallback(true);
      });
    });
  };

  const handleSubmitClick = () => {
    callGetState(async (state) => {
      const err = validator(state);
      if (err) {
        message.error(err);
        return;
      }
      callSetLoading(true);
      try {
        const requestData = helper.formatToSubmitData(state);
        const params = {
          ...requestData,
          changeReason: '其他', // 换房原因
          changeReasonCode: 'QT', // 换房原因码
          changeDetail: 'reservationChange',
        }; // 预订换排房临时写死
        await reserve.submitReserveExchange(params).then(
          () => {
            showPreSettleDrawer();
          },
          async (errRes) => {
            await handleRefreshErr(errRes);
            throw errRes;
          },
        );
      } catch (_) {
        callSetLoading(false);
      }
    });
  };

  // 数据初始化
  useEffect(() => {
    const { recNo, targetRoom, callInitState } = props;
    if (recNo) {
      callInitState(() => {
        return helper.getStateFromOrder(recNo, targetRoom).catch((e) => {
          unmountNode();
          throw e;
        });
      });
    } else {
      message.error(intl.get('reservation_error_config_invalid'));
      unmountNode();
    }
  }, []);

  if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader title={intl.get('drawer_exchange_reserve_title')} onClose={handleEditCancel} />
      <div className={styles.pageContainer}>
        <PopupTarget id={TargetType.ReserveExchange} />
        <RoomInfo />
        <RentRoomList />
      </div>
      <DrawerFooter>
        <SubmitBtn onSubmit={handleSubmitClick} />
        <Button onClick={handleEditCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
      <PreSettleDrawer ref={preSettleRef} />
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));

// export default () => {
//   const { showDrawer } = usePortalDrawer(Index);
//   useEffect(() => {
//     showDrawer({ recNo: 'J20220105530553' });
//   }, []);
//   return null;
// };
